<script setup lang="ts">
import type { CampaignFormErrors, FilterSelectOption, IdentificationData } from '../../types';

const model = defineModel<IdentificationData>({ required: true });

defineProps<{
  errors: CampaignFormErrors;
  incentiveOptions: FilterSelectOption[];
  isLoadingIncentives?: boolean;
}>();

function update<K extends keyof IdentificationData>(key: K, value: IdentificationData[K]) {
  model.value[key] = value;
}
</script>

<template>
  <div class="fs-section">
    <div class="fs-header">
      <div class="fs-num">1</div>
      <h4 class="fs-title">Identificação</h4>
    </div>
    <p class="fs-sub">Como esta campanha aparece nos relatórios e qual incentivo será aplicado.</p>

    <div class="row g-3">
      <div class="col-12">
        <label class="fs-label" for="campaignName">
          Nome da campanha <span class="fs-req">*</span>
        </label>
        <input
          id="campaignName"
          :value="model.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
          placeholder="Ex.: Cupom Loja 5% — Top 50 parceiros"
          @input="update('name', ($event.target as HTMLInputElement).value)"
        />
        <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
        <small v-else class="fs-hint">Visível só internamente.</small>
      </div>

      <div class="col-12">
        <label class="fs-label" for="incentive">
          Incentivo <span class="fs-req">*</span>
        </label>
        <select
          id="incentive"
          :value="model.incentive"
          class="form-select"
          :class="{ 'is-invalid': errors.incentive }"
          :disabled="isLoadingIncentives"
          @change="update('incentive', ($event.target as HTMLSelectElement).value)"
        >
          <option value="">
            {{ isLoadingIncentives ? 'Carregando incentivos…' : 'Selecione um incentivo…' }}
          </option>
          <option
            v-for="opt in incentiveOptions"
            :key="opt.id"
            :value="opt.id"
          >
            {{ opt.label }}
          </option>
        </select>
        <div v-if="errors.incentive" class="invalid-feedback">{{ errors.incentive }}</div>
        <small v-else class="fs-hint">
          Apenas incentivos habilitados pela tech aparecem aqui. Para criar ou habilitar, vá em
          <strong style="color: #00b569; cursor: pointer;">Incentivos</strong>.
        </small>
      </div>

      <div class="col-12">
        <label class="fs-label" for="hypothesis">
          Hipótese / Objetivo do teste <span class="fs-req">*</span>
        </label>
        <textarea
          id="hypothesis"
          :value="model.hypothesis"
          class="form-control"
          :class="{ 'is-invalid': errors.hypothesis }"
          rows="3"
          placeholder="Ex.: Acreditamos que parceiros com alto volume de CCBs vão revalidar mais
            quando recebem aumento de limite de crédito. Mediremos contra grupo controle."
          @input="update('hypothesis', ($event.target as HTMLTextAreaElement).value)"
        />
        <div v-if="errors.hypothesis" class="invalid-feedback">{{ errors.hypothesis }}</div>
        <small v-else class="fs-hint">Descreva a hipótese que será validada por esta campanha.</small>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fs-section {
  padding: 24px;
  border-bottom: 1px solid #e4e6eb;
}

.fs-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.fs-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00b569;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
}

.fs-title {
  font-size: 15px;
  font-weight: 700;
  color: #222222;
  margin: 0;
}

.fs-sub {
  font-size: 12.5px;
  color: #6b7280;
  margin-left: 36px;
  margin-bottom: 16px;
  line-height: 1.4;
}

.fs-label {
  font-size: 12px;
  font-weight: 600;
  color: #222222;
  margin-bottom: 4px;
  display: block;
}

.fs-req {
  color: #ef4444;
}

.fs-hint {
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.4;
  display: block;
  margin-top: 4px;
}

.form-control,
.form-select {
  padding: 9px 12px;
  border-color: #e4e6eb;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    border-color: #00b569;
    box-shadow: 0 0 0 3px rgba(0, 181, 105, 0.1);
  }

  &:disabled {
    background: #f5f6f8;
    color: #9ca3af;
  }
}
</style>
